import * as React from 'react';
import { Loader2, Eye, EyeOff, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getSeedreamConfig, saveSeedreamConfig } from '@/api/client';

export function SeedreamConfigTab() {
  const [config, setConfig] = React.useState<{
    provider: string;
    model: string;
    base_url: string;
    api_key: string;
  } | null>(null);
  const [showKey, setShowKey] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSeedreamConfig();
      setConfig(data.config || null);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSave = React.useCallback(async () => {
    if (!config) return;
    setSaving(true);
    setMsg('');
    try {
      await saveSeedreamConfig(config);
      setMsg('保存成功');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [config]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>图像模型配置</CardTitle>
          <CardDescription>配置 doubao-seedream-4.5 用于 AI 海报生成</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>图像模型配置</CardTitle>
        <CardDescription>配置 doubao-seedream-4.5 用于内容中心「一键生成海报」功能</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Provider</label>
            <Input
              value={config?.provider || ''}
              onChange={(e) => setConfig((c) => c ? { ...c, provider: e.target.value } : null)}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">模型</label>
            <Input
              value={config?.model || ''}
              onChange={(e) => setConfig((c) => c ? { ...c, model: e.target.value } : null)}
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Base URL</label>
          <Input
            placeholder="https://api.volcengine.com"
            value={config?.base_url || ''}
            onChange={(e) => setConfig((c) => c ? { ...c, base_url: e.target.value } : null)}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">API Key</label>
          <div className="flex gap-2">
            <Input
              type={showKey ? 'text' : 'password'}
              placeholder="输入 API Key"
              value={config?.api_key || ''}
              onChange={(e) => setConfig((c) => c ? { ...c, api_key: e.target.value } : null)}
              className="flex-1"
            />
            <Button variant="outline" size="icon" onClick={() => setShowKey((v) => !v)}>
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? '保存中…' : '保存配置'}
          </Button>
          {msg && <span className="text-sm text-muted-foreground">{msg}</span>}
        </div>
      </CardContent>
    </Card>
  );
}